import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { MessageCircle, Mail, Clock, LayoutTemplate, Copy, ExternalLink, Send, Trash2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, SectionCard } from "@/components/ui-helpers";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const KIND_LABEL = {
  email_verification: "Verification", verification_reminder: "Reminder",
  password_reset: "Password reset", system: "System",
};

const EMAIL_TEMPLATES = [
  { id: "quote_ready", label: "Quotation ready", body: "Hi {name}, your quotation is ready. Please review it here: {link}. Happy to tweak anything!" },
  { id: "payment_reminder", label: "Payment reminder", body: "Hi {name}, a gentle reminder that a payment for {title} is pending. Let me know if you need help." },
  { id: "order_ready", label: "Order confirmed", body: "Hi {name}, great news — your order {title} is confirmed! Your documents are attached. Thank you for shopping with us!" },
];

export default function MessageCenter() {
  const [tab, setTab] = useState("sent");
  const [items, setItems] = useState(null);
  const [waTemplates, setWaTemplates] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [cf, setCf] = useState({ customer_id: "", to: "", to_name: "", subject: "", body: "" });
  const [sending, setSending] = useState(false);
  const { user } = useAuth();
  const [sp] = useSearchParams();
  const [filter, setFilter] = useState("all");
  const [savedTpls, setSavedTpls] = useState([]);
  const [ntpl, setNtpl] = useState({ title: "", body: "" });
  const shownItems = (items || []).filter((m) => filter === "all" ? true : filter === "failed" ? m.status === "failed" : m.channel === filter);

  const loadLog = () => Promise.all([
    api.get("/whatsapp/log").catch(() => ({ data: { messages: [] } })),
    api.get("/messages/emails").catch(() => ({ data: { emails: [] } })),
  ]).then(([wa, em]) => {
    const merged = [
      ...wa.data.messages.map((m) => ({ ...m, channel: "whatsapp", title: m.recipient_name || m.recipient_phone || "WhatsApp", text: m.text })),
      ...em.data.emails.map((e) => ({ ...e, channel: "email", title: e.to_name || e.to, text: e.subject })),
    ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    setItems(merged);
  });

  useEffect(() => {
    loadLog();
    api.get("/whatsapp/config").then((r) => setWaTemplates(r.data.templates || [])).catch(() => {});
    api.get("/records?type=customer").then((r) => setCustomers(r.data.records || [])).catch(() => {});
    api.get("/records?type=quotation").then((r) => setQuotes(r.data.records || [])).catch(() => {});
    api.get("/records?type=email_template").then((r) => setSavedTpls(r.data.records || [])).catch(() => {});
  }, []);

  const saveTpl = async () => {
    if (!ntpl.title.trim() || !ntpl.body.trim()) { toast.error("Name and message are required."); return; }
    try {
      await api.post("/records", { type: "email_template", title: ntpl.title, data: { body: ntpl.body } });
      setNtpl({ title: "", body: "" });
      const r = await api.get("/records?type=email_template"); setSavedTpls(r.data.records || []);
      toast.success("Template saved");
    } catch (e) { toast.error("Could not save template"); }
  };
  const delTpl = async (id) => {
    try { await api.delete(`/records/${id}`); setSavedTpls((s) => s.filter((x) => x.record_id !== id)); } catch (e) { toast.error("Could not delete template"); }
  };

  useEffect(() => {
    const qid = sp.get("quote");
    if (qid && quotes.length) { setTab("compose"); attachQuote(qid); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sp, quotes]);

  const pickCustomer = (id) => {
    const c = customers.find((x) => x.record_id === id);
    setCf((f) => ({ ...f, customer_id: id, to: c?.data?.email || "", to_name: c?.title || "" }));
  };

  const sendEmail = async () => {
    if (!cf.to || !cf.subject.trim() || !cf.body.trim()) { toast.error("Recipient, subject and message are required."); return; }
    setSending(true);
    try {
      await api.post("/messages/send-email", cf);
      toast.success("Email sent");
      setCf({ customer_id: "", to: "", to_name: "", subject: "", body: "" });
      setTab("sent"); loadLog();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not send email"); }
    finally { setSending(false); }
  };

  const openInMyEmail = () => {
    if (!cf.to || !cf.subject.trim() || !cf.body.trim()) { toast.error("Recipient, subject and message are required."); return; }
    const subj = (user?.mail_subject ? user.mail_subject + " " : "") + cf.subject;
    const body = cf.body + (user?.mail_signature ? `\n\n${user.mail_signature}` : "");
    window.location.href = `mailto:${encodeURIComponent(cf.to)}?subject=${encodeURIComponent(subj)}&body=${encodeURIComponent(body)}`;
    toast.success("Opening your email app — sent from your own address, not Suffle.");
  };

  const copy = async (t) => {
    try { await navigator.clipboard.writeText(t); toast.success("Copied to clipboard"); }
    catch (e) { toast.error("Couldn't copy — please copy manually."); }
  };

  const retry = async (logId) => {
    try { await api.post("/messages/retry-email", { log_id: logId }); toast.success("Email resent"); loadLog(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Retry failed"); }
  };

  const retryAll = async () => {
    try { const { data } = await api.post("/messages/retry-all-failed"); toast.success(`Retried ${data.retried}/${data.total} failed email(s)`); loadLog(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not retry the failed emails."); }
  };

  const attachQuote = async (id) => {
    const q = quotes.find((x) => x.record_id === id); if (!q) return;
    try {
      const { data } = await api.post(`/quotations/${id}/share`);
      const link = `${window.location.origin}/q/${data.token}`;
      const price = q.data?.customer_price ? `\nPrice: ₹${Number(q.data.customer_price).toLocaleString("en-IN")}` : "";
      setCf((f) => ({ ...f, subject: f.subject || `Your quotation: ${q.title}`, body: (f.body ? f.body + "\n\n" : "") + `Here is your quotation: ${q.title}${price}\nView & respond: ${link}` }));
      toast.success("Quotation attached to email");
    } catch (e) { toast.error("Could not attach quotation"); }
  };

  return (
    <div className="mx-auto max-w-3xl">
      <PageHeader title="Message Center" icon={Mail}
        subtitle="Every message Suffle sends — WhatsApp and email — in one auditable log, plus reusable templates." />

      <div className="mb-5 inline-flex rounded-full border border-slate-200 bg-white p-1">
        {[["sent", "Sent Log"], ["compose", "Compose Email"], ["templates", "Templates"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`msg-tab-${k}`}
            className={`rounded-full px-4 py-1.5 text-sm font-medium transition ${tab === k ? "brand-gradient-bg text-white" : "text-slate-500 hover:text-slate-700"}`}>{l}</button>
        ))}
      </div>

      {tab === "sent" && (
        <SectionCard testid="msg-sent-log">
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-1.5">
              {[["all", "All"], ["whatsapp", "WhatsApp"], ["email", "Email"], ["failed", "Failed"]].map(([k, l]) => (
                <button key={k} onClick={() => setFilter(k)} data-testid={`msg-filter-${k}`}
                  className={`rounded-full px-2.5 py-1 text-[11px] font-medium transition ${filter === k ? "bg-slate-800 text-white" : "border border-slate-200 text-slate-500 hover:border-slate-300"}`}>{l}</button>
              ))}
            </div>
            {items && items.some((m) => m.channel === "email" && m.status === "failed" && m.kind === "agent_email" && m.log_id) && (
              <button onClick={retryAll} data-testid="retry-all-failed" className="rounded-full border border-rose-200 px-3 py-1 text-[11px] font-semibold text-rose-600 hover:bg-rose-50">Retry all failed</button>
            )}
          </div>
          {items === null ? (
            <p className="py-3 text-sm text-slate-400">Loading messages…</p>
          ) : shownItems.length === 0 ? (
            <p className="py-3 text-sm text-slate-400">No messages yet. Compose one from WhatsApp or send an email-driven action.</p>
          ) : (
            <div className="divide-y divide-slate-50">
              {shownItems.map((m, i) => (
                <div key={m.log_id || m._id || m.id || i} className="flex items-start gap-3 py-3" data-testid="msg-item">
                  <span className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${m.channel === "whatsapp" ? "bg-emerald-50 text-emerald-500" : "bg-blue-50 text-blue-500"}`}>
                    {m.channel === "whatsapp" ? <MessageCircle className="h-4 w-4" /> : <Mail className="h-4 w-4" />}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <p className="truncate text-sm font-medium text-slate-700">{m.title}</p>
                      <span className="shrink-0 text-[11px] text-slate-400">{new Date(m.created_at).toLocaleString("en-GB")}</span>
                    </div>
                    <p className="truncate text-xs text-slate-500">{m.text}</p>
                    <span className={`mt-1 inline-block rounded-full px-2 py-0.5 text-[10px] font-medium ${m.status === "failed" ? "bg-rose-50 text-rose-500" : "bg-slate-100 text-slate-500"}`}>
                      {m.status === "failed" ? "Failed" : m.channel === "whatsapp" ? "WhatsApp" : (KIND_LABEL[m.kind] || "Email")}
                    </span>
                    {m.channel === "email" && m.status === "failed" && m.kind === "agent_email" && m.log_id && (
                      <button onClick={() => retry(m.log_id)} data-testid="retry-email" className="ml-2 rounded-full border border-rose-200 px-2 py-0.5 text-[10px] font-medium text-rose-600 hover:bg-rose-50">Retry</button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </SectionCard>
      )}

      {tab === "compose" && (
        <SectionCard testid="msg-compose">
          <div className="space-y-3">
            <div>
              <Label className="text-xs text-slate-500">Customer</Label>
              <Select value={cf.customer_id} onValueChange={pickCustomer}>
                <SelectTrigger className="mt-1" data-testid="compose-customer"><SelectValue placeholder="Pick a customer (or type an email below)" /></SelectTrigger>
                <SelectContent>{customers.map((c) => <SelectItem key={c.record_id} value={c.record_id}>{c.title}{c.data?.email ? ` · ${c.data.email}` : ""}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-500">Attach a quotation (optional)</Label>
              <Select value="" onValueChange={attachQuote}>
                <SelectTrigger className="mt-1" data-testid="compose-quote"><SelectValue placeholder="Add a quote's share link + price to the message" /></SelectTrigger>
                <SelectContent>{quotes.map((q) => <SelectItem key={q.record_id} value={q.record_id}>{q.title}{q.data?.customer_price ? ` · ₹${Number(q.data.customer_price).toLocaleString("en-IN")}` : ""}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div><Label className="text-xs text-slate-500">To (email)</Label><Input value={cf.to} onChange={(e) => setCf({ ...cf, to: e.target.value })} data-testid="compose-to" className="mt-1" placeholder="customer@email.com" /></div>
              <div><Label className="text-xs text-slate-500">Recipient name</Label><Input value={cf.to_name} onChange={(e) => setCf({ ...cf, to_name: e.target.value })} className="mt-1" /></div>
            </div>
            <div><Label className="text-xs text-slate-500">Subject</Label><Input value={cf.subject} onChange={(e) => setCf({ ...cf, subject: e.target.value })} data-testid="compose-subject" className="mt-1" placeholder="Your quotation" /></div>
            <div><Label className="text-xs text-slate-500">Message</Label><Textarea rows={6} value={cf.body} onChange={(e) => setCf({ ...cf, body: e.target.value })} data-testid="compose-body" className="mt-1" placeholder="Write your message..." /></div>
            <div className="flex flex-wrap gap-2">
              {EMAIL_TEMPLATES.map((t) => <button key={t.id} type="button" onClick={() => setCf((f) => ({ ...f, body: t.body }))} className="rounded-full border border-slate-200 px-3 py-1 text-xs text-slate-600 hover:border-blue-300" data-testid={`compose-tpl-${t.id}`}>{t.label}</button>)}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button onClick={openInMyEmail} data-testid="compose-open-mail" className="inline-flex items-center gap-2 rounded-full brand-gradient-bg px-5 py-2.5 text-sm font-semibold text-white"><Mail className="h-4 w-4" /> Open in my email app</button>
              <button onClick={sendEmail} disabled={sending} data-testid="compose-send" className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-600 hover:border-blue-300 disabled:opacity-50"><Send className="h-4 w-4" />{sending ? "Sending..." : "Send via Suffle instead"}</button>
            </div>
            <p className="text-[11px] text-slate-400"><b>Open in my email app</b> sends from your own address (Gmail/Outlook/etc.) — Suffle sends nothing. “Send via Suffle” uses Suffle's service and is recorded in your Sent Log.</p>
          </div>
        </SectionCard>
      )}

      {tab === "templates" && (
        <div className="space-y-4">
          <SectionCard title="WhatsApp templates">
            <div className="mb-2 flex items-center gap-2 text-xs text-slate-400"><LayoutTemplate className="h-3.5 w-3.5" /> Reuse in the WhatsApp composer</div>
            <div className="space-y-2">
              {waTemplates.filter((t) => t.body).map((t) => (
                <div key={t.id} className="rounded-xl border border-slate-100 p-3" data-testid={`wa-tpl-${t.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-semibold text-slate-700">{t.label}</p>
                    <div className="flex gap-1">
                      <button onClick={() => copy(t.body)} title="Copy" className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-50 hover:text-slate-600"><Copy className="h-4 w-4" /></button>
                      <Link to="/agent/whatsapp" title="Open composer" className="rounded-lg p-1.5 text-slate-400 hover:bg-emerald-50 hover:text-emerald-600"><ExternalLink className="h-4 w-4" /></Link>
                    </div>
                  </div>
                  <p className="mt-1 whitespace-pre-line text-xs text-slate-500">{t.body}</p>
                </div>
              ))}
            </div>
          </SectionCard>
          <SectionCard title="Email templates">
            <div className="space-y-2">
              {EMAIL_TEMPLATES.map((t) => (
                <div key={t.id} className="rounded-xl border border-slate-100 p-3" data-testid={`email-tpl-${t.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-semibold text-slate-700">{t.label}</p>
                    <button onClick={() => copy(t.body)} title="Copy" className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-50 hover:text-slate-600"><Copy className="h-4 w-4" /></button>
                  </div>
                  <p className="mt-1 text-xs text-slate-500">{t.body}</p>
                </div>
              ))}
              {savedTpls.map((t) => (
                <div key={t.record_id} className="rounded-xl border border-indigo-100 bg-indigo-50/40 p-3" data-testid="saved-tpl">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-semibold text-slate-700">{t.title} <span className="text-[10px] text-indigo-400">· yours</span></p>
                    <div className="flex gap-1">
                      <button onClick={() => copy(t.data?.body || "")} title="Copy" className="rounded-lg p-1.5 text-slate-400 hover:bg-white hover:text-slate-600"><Copy className="h-4 w-4" /></button>
                      <button onClick={() => delTpl(t.record_id)} title="Delete" data-testid="del-tpl" className="rounded-lg p-1.5 text-slate-400 hover:bg-rose-50 hover:text-rose-500"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </div>
                  <p className="mt-1 whitespace-pre-line text-xs text-slate-500">{t.data?.body}</p>
                </div>
              ))}
              <div className="rounded-xl border border-dashed border-slate-200 p-3">
                <p className="text-xs font-semibold text-slate-600">Create your own template</p>
                <Input value={ntpl.title} onChange={(e) => setNtpl({ ...ntpl, title: e.target.value })} data-testid="tpl-name" className="mt-2" placeholder="Template name" />
                <Textarea rows={3} value={ntpl.body} onChange={(e) => setNtpl({ ...ntpl, body: e.target.value })} data-testid="tpl-body" className="mt-2" placeholder="Message body — use {name}, {title}, {link}" />
                <button onClick={saveTpl} data-testid="tpl-save" className="mt-2 rounded-full brand-gradient-bg px-4 py-2 text-xs font-semibold text-white">Save template</button>
              </div>
            </div>
          </SectionCard>
        </div>
      )}
    </div>
  );
}
